import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, Video, Image as ImageIcon, Music, Table, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const KIND_ICON = { video: Video, document: FileText, image: ImageIcon, audio: Music, spreadsheet: Table, text: FileText };
const ACCEPT = "video/*,image/*,audio/*,application/pdf,.csv,.tsv,.xlsx,.xls,.txt,.json";

function Result({ r }) {
  if (!r) return null;
  const stats = r.stats && Object.keys(r.stats).length ? r.stats : null;
  return (
    <div className="text-sm space-y-2">
      {r.title && <div className="font-heading font-bold">{r.title}</div>}
      {r.summary && <p className="text-muted-foreground">{r.summary}</p>}
      {stats && (
        <div className="flex flex-wrap gap-2" data-testid="analysis-stats">
          {Object.entries(stats).map(([k, v]) => (
            <span key={k} className="border border-border px-2 py-1 text-xs"><span className="text-muted-foreground">{k}:</span> <b className="tabular-nums">{String(v)}</b></span>
          ))}
        </div>
      )}
      {r.text && <div className="text-xs bg-surface border border-border p-2 max-h-40 overflow-auto whitespace-pre-wrap">{r.text}</div>}
      {Array.isArray(r.events) && r.events.length > 0 && (
        <div className="text-xs text-muted-foreground">{r.events.slice(0, 8).map((e, i) => <span key={i} className="mr-3">{e.t_sec != null ? `${e.t_sec}s` : ""} {e.label}</span>)}</div>
      )}
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
        {typeof r.confidence === "number" && <span>confidence {r.confidence}%</span>}
        {r.caveats?.length > 0 && <span className="text-traffic-amber">⚠ {r.caveats.join("; ")}</span>}
      </div>
    </div>
  );
}

export default function FileAnalysis({ athleteId }) {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [past, setPast] = useState([]);
  const inputRef = useRef();

  const load = useCallback(() => {
    if (athleteId) api.get(`/athletes/${athleteId}/media/analyses`).then((r) => setPast(r.data.analyses || [])).catch(() => {});
  }, [athleteId]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!file) return toast.error("Choose a file first");
    if (file.type.startsWith("video/") || /\.(mp4|mov|webm|avi|mkv)$/i.test(file.name)) {
      toast.info("Video analysis now starts with physics in Strategy");
      navigate(`/athlete/${athleteId}?tab=strategy`);
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("prompt", prompt);
    fd.append("assign_to", assignTo);
    try {
      const { data } = await api.post(`/athletes/${athleteId}/media/analyze`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 600000,
      });
      toast.success(`Analysed · ${data.cost} credits used`);
      setFile(null); setPrompt(""); if (inputRef.current) inputRef.current.value = "";
      load();
      window.dispatchEvent(new CustomEvent("credits-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setBusy(false); }
  };

  const grouped = past.reduce((acc, a) => { (acc[a.kind] = acc[a.kind] || []).push(a); return acc; }, {});

  return (
    <div className="border border-border mb-6" data-testid="zone-file-analysis">
      <button onClick={() => setOpen((o) => !o)} className="w-full px-5 py-4 border-b border-border flex items-center gap-2 hover:bg-surface transition-colors" data-testid="file-analysis-toggle">
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Sparkles className="w-4 h-4" />
        <h3 className="font-heading font-bold tracking-tight flex-1 text-left">Training files &amp; notes</h3>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">documents · audio · extraction history</span>
      </button>
      {open && (
        <div className="p-5">
          <button data-testid="open-strategy-film" onClick={() => navigate(`/athlete/${athleteId}?tab=strategy`)} className="mb-4 text-xs border border-border rounded-full px-3 py-2 hover:bg-surface">Open physics-first film analysis in Strategy</button>
          <>
          <div
            onClick={() => inputRef.current?.click()}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]); }}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-border hover:border-foreground transition-colors cursor-pointer p-6 text-center"
            data-testid="file-drop"
          >
            <UploadCloud className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
            <div className="text-sm">{file ? file.name : "Drop or click · video, photo, PDF, scoreboard, audio, CSV, spreadsheet, anything"}</div>
            <div className="text-[11px] text-muted-foreground mt-1">Video costs scale with length; documents & audio are a flat extract.</div>
            <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" data-testid="file-input" onChange={(e) => setFile(e.target.files[0])} />
          </div>

          <textarea
            data-testid="analysis-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
            placeholder="What should the AI pull out? e.g. 'Read the scoreboard and final score', 'Count forehands vs backhands', 'OCR this lab report', 'Extract the sets/reps/weights table'"
            className="inp w-full mt-3 resize-none"
          />
          <div className="flex flex-wrap gap-3 mt-3 items-center">
            <input data-testid="analysis-assign" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} placeholder="Assign to activity (optional)" className="inp flex-1 min-w-[180px]" />
            <button data-testid="analysis-run" onClick={run} disabled={busy || !file} className="bg-foreground text-background px-5 py-2 text-sm flex items-center gap-2 hover:bg-neutral-800 transition-colors disabled:opacity-40">
              <Sparkles className={`w-4 h-4 ${busy ? "animate-pulse" : ""}`} /> {busy ? "Analysing…" : "Analyse"}
            </button>
          </div>
          {busy && <p className="text-[11px] text-muted-foreground mt-2" data-testid="analysis-thinking">Reading the file and extracting what you asked for · larger videos take longer.</p>}

          {past.length > 0 && (
            <div className="mt-6 space-y-4" data-testid="analysis-history">
              {Object.entries(grouped).map(([kind, items]) => {
                const Icon = KIND_ICON[kind] || FileText;
                return (
                  <div key={kind}>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-2"><Icon className="w-3.5 h-3.5" /> {kind} · {items.length}</div>
                    <div className="space-y-2">
                      {items.map((a) => (
                        <div key={a.analysis_id} className="border border-border p-3" data-testid={`analysis-${a.analysis_id}`}>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                            <span className="truncate flex-1">{a.filename}</span>
                            {a.assign_to && <span className="border border-border px-1.5 py-0.5">{a.assign_to}</span>}
                            <span>{a.cost} cr</span>
                          </div>
                          {a.prompt && <div className="text-[11px] italic text-muted-foreground mb-2">“{a.prompt}”</div>}
                          <Result r={a.result} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </>
        </div>
      )}
    </div>
  );
}
